import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useCurrentScope } from '../../../shared/scope/useCurrentScope.js';
import {
  getDealsQueryKey,
  type DealListItem,
  type DealStage,
  getDealDetailQueryKey,
  type DealDetail,
  updateDealStage,
} from '../api/deals.client.js';
import { moveDealStage } from '../lib/kanban.js';

interface MutationInput {
  dealId: string;
  toStage: DealStage;
  reason?: string;
}

interface MutationContext {
  previousDeals: DealListItem[] | undefined;
  // Привязано к фабрике ключа — состав ключа может меняться (фильтры).
  queryKey: ReturnType<typeof getDealsQueryKey>;
  previousDetail: DealDetail | undefined;
  detailKey: readonly string[];
}

export function useDealStageMutation() {
  const queryClient = useQueryClient();
  const scope = useCurrentScope();
  const workspaceId = scope?.workspaceId ?? 'anonymous';
  const queryKey = getDealsQueryKey(workspaceId);

  return useMutation<DealListItem, Error, MutationInput, MutationContext>({
    mutationFn: async ({ dealId, toStage, reason }) =>
      updateDealStage(dealId, { toStage, ...(reason ? { reason } : {}) }),
    onMutate: async ({ dealId, toStage }) => {
      await queryClient.cancelQueries({ queryKey });
      const previousDeals = queryClient.getQueryData<DealListItem[]>(queryKey);
      const detailKey = getDealDetailQueryKey(workspaceId, dealId);
      await queryClient.cancelQueries({ queryKey: detailKey });
      const previousDetail = queryClient.getQueryData<DealDetail>(detailKey);
      if (previousDetail)
        queryClient.setQueryData(detailKey, { ...previousDetail, stage: toStage });

      if (previousDeals !== undefined) {
        queryClient.setQueryData<DealListItem[]>(
          queryKey,
          moveDealStage(previousDeals, dealId, toStage),
        );
      }

      return { previousDeals, queryKey, previousDetail, detailKey };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousDeals !== undefined) {
        queryClient.setQueryData(context.queryKey, context.previousDeals);
      }
      if (context?.previousDetail)
        queryClient.setQueryData(context.detailKey, context.previousDetail);
    },
    onSettled: async () => {
      // Префикс: сбрасываем и отфильтрованные списки сделок тоже.
      await queryClient.invalidateQueries({ queryKey: ['deals', workspaceId] });
      await queryClient.invalidateQueries({ queryKey: ['activities', workspaceId] });
    },
  });
}
